import React, {useCallback, useLayoutEffect, useMemo, useState} from "react";
import {
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    MenuItem,
    TextField,
    Typography,
    Unstable_Grid2 as Grid
} from "@mui/material";
import useIsMounted from "../../../hooks/use-is-mounted";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";


const RemoveConfirmationDialog = ({
                                      open,
                                      close,
                                      remove,
                                      data,
                                      removedEntryTitle,
                                  }) => {
    const [removing, setRemoving] = useState(false);
    const [replacement, setReplacement] = useState(null);
    const isMounted = useIsMounted();

    const needsToReplace = useMemo(() => !!data?.length, [data?.length]);

    /**
     * As soon as the dialog opens, resets the state values.
     */
    useLayoutEffect(() => {
        if (open) {
            setRemoving(false);
            setReplacement(null);
        }
    }, [open])

    /**
     * Closes the dialog by dispatching its relevant action to the redux store.
     */
    const closeDialog = useCallback(async () => {
        setRemoving(true);
        const resultFlag = await remove(replacement);
        if (!isMounted())
            return;
        setRemoving(false);
        if (resultFlag) {
            close();
        }
    }, [close, isMounted, remove, replacement])

    return (
        <>
            <Dialog open={open} setOpen={close}>
                <DialogTitle fontWeight={600}>
                    Remove Confirmation
                </DialogTitle>
                <DialogContent dividers>
                    {
                        !needsToReplace &&
                        <DialogContentText>
                            Are you sure you want to remove {removedEntryTitle ?? "the selected record"}?
                        </DialogContentText>
                    }
                    {
                        needsToReplace &&
                        <>
                            <DialogContentText variant={'body1'}>
                                Please select the replacement record before deleting the requested record:
                            </DialogContentText>
                            <Grid container xs={12} gap={2} spacing={2} marginInline={0} marginBlock={2}>
                                <Grid xs={'auto'} padding={0}>
                                    <Typography variant={'body1'}>
                                        To be removed:
                                    </Typography>
                                </Grid>
                                <Grid xs padding={0}>
                                    <Typography variant={'body1'}>
                                        {removedEntryTitle ?? "the selected record"}
                                    </Typography>
                                </Grid>
                                <Grid xs={12} padding={0}/>
                                <Grid xs={'auto'} padding={0} sx={{display: 'flex', alignItems: 'center'}}>
                                    <Typography variant={'body1'}>
                                        To be replaced with:
                                    </Typography>
                                </Grid>
                                <Grid xs padding={0}>
                                    <TextField
                                        select
                                        fullWidth
                                        label={"Replacement"}
                                        value={replacement?.id ?? ""}
                                        onChange={(e) => setReplacement(data?.find(a => a.id === e.target.value) ?? null)}
                                        SelectProps={{
                                            renderValue: (value) => data?.find(e => e.id === value)?.title ?? "",
                                        }}
                                    >
                                        {
                                            data?.map(e => (
                                                <MenuItem key={e.id} value={e.id}>
                                                    {e.title}
                                                </MenuItem>
                                            ))
                                        }
                                    </TextField>
                                </Grid>
                            </Grid>
                        </>
                    }
                </DialogContent>
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', padding: 0}}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => close()}
                        borderRadius={'0 8px 0 8px'}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={closeDialog}
                        disabled={(needsToReplace && !replacement) || removing}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {
                            removing
                                ? "Removing..."
                                : "Remove".concat(needsToReplace ? " And Replace" : "")
                        }
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default RemoveConfirmationDialog;
